// Pure polyline geometry helpers for arc rendering. No OpenLayers imports —
// unit-tested with `node --test`. Coordinates are [x, y] in map units (EPSG:3857).

// Right-of-travel normal of heading (tx, ty) is (ty, -tx).
function rightNormal(tx, ty) {
  const len = Math.hypot(tx, ty);
  if (len === 0) return [0, 0];
  return [ty / len, -tx / len];
}

// Per-vertex tangent via central difference (endpoints use their one segment).
function tangentAt(coords, i) {
  const prev = coords[Math.max(0, i - 1)];
  const next = coords[Math.min(coords.length - 1, i + 1)];
  return [next[0] - prev[0], next[1] - prev[1]];
}

/**
 * Sign of the polyline's net turning (sum of segment cross products):
 * +1 = left/CCW, −1 = right/CW, 0 = straight (or fewer than 3 vertices).
 * Map coordinates (y up). Used to find the inside of a curved arc's bend.
 */
export function turningSign(coords) {
  let sum = 0;
  for (let i = 1; i < coords.length - 1; i++) {
    const ax = coords[i][0] - coords[i - 1][0];
    const ay = coords[i][1] - coords[i - 1][1];
    const bx = coords[i + 1][0] - coords[i][0];
    const by = coords[i + 1][1] - coords[i][1];
    sum += ax * by - ay * bx;
  }
  return Math.sign(sum);
}

/**
 * Rubber-sheet the polyline so its first vertex lands on `startNode` and its last on
 * `endNode`, distributing each end's correction linearly by arc-length fraction.
 * Used to pull a group's overlapping arcs onto shared junction nodes (their raw
 * endpoints drift ~0.5m apart) so they meet before being bowed apart. New array;
 * input not mutated. A <2-point line is returned copied.
 */
export function snapEndpoints(coords, startNode, endNode) {
  const n = coords.length;
  if (n < 2) return coords.map((c) => [c[0], c[1]]);
  const first = coords[0];
  const last = coords[n - 1];
  const dsx = startNode[0] - first[0];
  const dsy = startNode[1] - first[1];
  const dex = endNode[0] - last[0];
  const dey = endNode[1] - last[1];

  const cum = [0];
  let total = 0;
  for (let i = 1; i < n; i++) {
    total += Math.hypot(
      coords[i][0] - coords[i - 1][0],
      coords[i][1] - coords[i - 1][1],
    );
    cum.push(total);
  }

  return coords.map((c, i) => {
    const s = total === 0 ? 0 : cum[i] / total;
    return [c[0] + (1 - s) * dsx + s * dex, c[1] + (1 - s) * dsy + s * dey];
  });
}

/**
 * Shift every vertex along its local right-of-travel normal by `distance` map-units
 * (signed: + = right of travel, − = left) — a uniform PARALLEL offset, including the
 * endpoints. Used to separate an overlapping pair so they run side by side the whole
 * length. A constant (un-tapered) offset is deliberate: it keeps the body straight
 * and, crucially, leaves the end tangent undistorted, so the hover curvature circle —
 * which reads its tangent from the arc's last segment — parallel-shifts to hug the
 * offset line instead of being rotated off it by a taper ramp. Returns a new array; a
 * line with no interior vertex (2 points) is returned unshifted (guards the hover
 * tangent probe). Coordinates are not mutated.
 */
export function offsetPolyline(coords, distance) {
  const n = coords.length;
  if (n < 3) return coords.map((c) => [c[0], c[1]]);
  return coords.map((c, i) => {
    const [tx, ty] = tangentAt(coords, i);
    const [nx, ny] = rightNormal(tx, ty);
    return [c[0] + nx * distance, c[1] + ny * distance];
  });
}

/**
 * Point + heading at `fraction` (0..1) of the polyline's total length.
 * heading = atan2(dy, dx) of the segment that contains the point.
 */
export function sampleAlong(coords, fraction) {
  if (coords.length < 2) {
    return { point: [coords[0][0], coords[0][1]], rotation: 0 };
  }
  const segLengths = [];
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const d = Math.hypot(
      coords[i + 1][0] - coords[i][0],
      coords[i + 1][1] - coords[i][1],
    );
    segLengths.push(d);
    total += d;
  }
  const target = Math.max(0, Math.min(1, fraction)) * total;
  let acc = 0;
  for (let i = 0; i < segLengths.length; i++) {
    const next = acc + segLengths[i];
    if (next >= target || i === segLengths.length - 1) {
      const t = segLengths[i] === 0 ? 0 : (target - acc) / segLengths[i];
      const dx = coords[i + 1][0] - coords[i][0];
      const dy = coords[i + 1][1] - coords[i][1];
      return {
        point: [coords[i][0] + dx * t, coords[i][1] + dy * t],
        rotation: Math.atan2(dy, dx),
      };
    }
    acc = next;
  }
  // Unreachable for length >= 2, but keep a defined return.
  const last = coords[coords.length - 1];
  return { point: [last[0], last[1]], rotation: 0 };
}
